<template>
  <section class="panel weather-panel">
    <div class="weather-header">
      <i class="fa-solid fa-location-dot location-icon"></i>
      {{ weather.location || '위치 확인 중' }}
    </div>

    <hr class="divider" />

    <div class="weather-body">
      <div class="weather-left">
        <img class="weather-main-icon" :src="getIconUrl(weather.icon)" alt="weather" />
        <div class="weather-desc">
          {{ weather.description || '-' }}
        </div>
      </div>

      <div class="weather-right">
        <div class="date">{{ nowDate }}</div>
        <div class="time">{{ nowTime }}</div>

        <div class="weather-temp">
          {{ weather.temp !== null ? Math.round(weather.temp) : '-' }}°
        </div>

        <div class="today-range">
          <span class="hi">{{ todayMax !== null ? Math.round(todayMax) : '-' }}°</span>
          <span class="slash"> / </span>
          <span class="lo">{{ todayMin !== null ? Math.round(todayMin) : '-' }}°</span>
        </div>
      </div>
    </div>

    <hr class="divider" />

    <div class="weather-week week-strip">
      <div class="week-day" v-for="(day, index) in weeklyWeather" :key="index">
        <div class="week-name">
          {{ day.day }}
        </div>

        <img class="week-icon" :src="getIconUrl(day.icon)" alt="weather icon" />

        <div class="week-temp">
          <span class="hi">{{ Math.round(day.max) }}°</span>
          <span class="slash">/</span>
          <span class="lo">{{ Math.round(day.min) }}°</span>
        </div>
      </div>
    </div>
  </section>

  <section class="panel calendar-panel">
    <DatePicker
      v-model="selectedDate"
      inline
      :showWeek="false"
      weekdayFormat="narrow"
      class="right-calendar"
      dateFormat="mm/yy"
    />
  </section>
</template>

<script>
export default {
  name: 'WeatherPanel',

  data() {
    return {
      weather: {
        location: '',
        temp: null,
        description: '',
        icon: ''
      },

      weeklyWeather: [],
      nowDate: '',
      nowTime: '',
      todayMin: null,
      todayMax: null,

      lat: 37.5665,
      lon: 126.978,
      apiKey: '5cbdf5377524cf2fc0e78dc15db0de00',

      selectedDate: new Date(),
      clockTimer: null
    }
  },

  async mounted() {
    this.updateNow()
    this.clockTimer = setInterval(this.updateNow, 1000)

    await this.initWeatherByCurrentLocation()
  },

  beforeUnmount() {
    if (this.clockTimer) {
      clearInterval(this.clockTimer)
      this.clockTimer = null
    }
  },

  methods: {
    async fetchCurrentWeather() {
      try {
        const url =
          `https://api.openweathermap.org/data/2.5/weather` +
          `?lat=${this.lat}&lon=${this.lon}` +
          `&units=metric&lang=kr&appid=${this.apiKey}`

        const res = await fetch(url)
        const data = await res.json()

        this.weather = {
          location: data?.name || '',
          temp: data?.main?.temp ?? null,
          description: data?.weather?.[0]?.description || '',
          icon: data?.weather?.[0]?.icon || ''
        }
      } catch (err) {
        console.error('현재 날씨 조회 실패', err)
      }
    },

    getCurrentPosition() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('이 브라우저에서는 위치 정보를 지원하지 않습니다.'))
          return
        }

        navigator.geolocation.getCurrentPosition(
          position => {
            resolve({
              lat: position.coords.latitude,
              lon: position.coords.longitude
            })
          },
          error => {
            reject(error)
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        )
      })
    },

    async initWeatherByCurrentLocation() {
      try {
        const coords = await this.getCurrentPosition()
        this.lat = coords.lat
        this.lon = coords.lon
      } catch (err) {
        console.error('현재 위치 조회 실패', err)
      }

      await this.fetchCurrentWeather()
      await this.fetchForecast()
    },

    async fetchForecast() {
      try {
        const url =
          `https://api.openweathermap.org/data/2.5/forecast` +
          `?lat=${this.lat}&lon=${this.lon}` +
          `&units=metric&lang=kr&appid=${this.apiKey}`

        const res = await fetch(url)
        const data = await res.json()

        if (!data?.list) return

        const dayMap = {}

        data.list.forEach(item => {
          const date = item.dt_txt.split(' ')[0]

          if (!dayMap[date]) {
            dayMap[date] = {
              temps: [],
              icon: item.weather?.[0]?.icon || ''
            }
          }

          dayMap[date].temps.push(item.main.temp)
        })

        this.weeklyWeather = Object.keys(dayMap)
          .slice(1, 6)
          .map(date => {
            const temps = dayMap[date].temps
            return {
              day: this.formatDay(date),
              min: Math.min(...temps),
              max: Math.max(...temps),
              icon: dayMap[date].icon
            }
          })

        const now = new Date()
        const yyyy = now.getFullYear()
        const MM = String(now.getMonth() + 1).padStart(2, '0')
        const dd = String(now.getDate()).padStart(2, '0')
        const today = `${yyyy}-${MM}-${dd}`

        const todayItems = data.list.filter(item => item.dt_txt.startsWith(today))

        if (todayItems.length) {
          const temps = todayItems.map(item => item.main.temp)
          this.todayMin = Math.min(...temps)
          this.todayMax = Math.max(...temps)
        }
      } catch (err) {
        console.error('주간 날씨 조회 실패', err)
      }
    },

    updateNow() {
      const now = new Date()

      this.nowDate = now.toLocaleDateString('ko-KR', {
        month: 'long',
        day: 'numeric',
        weekday: 'short'
      })

      this.nowTime = now.toLocaleTimeString('ko-KR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatDay(dateText) {
      const date = new Date(dateText)
      return date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()
    },

    getIconUrl(icon) {
      if (!icon) return ''
      return `https://openweathermap.org/img/wn/${icon}@2x.png`
    }
  }
}
</script>

<style scoped>
.panel {
  background: #ffffff;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.divider {
  border: none;
  border-top: 1px solid #e5e5e5;
  margin: 5px 0;
}

.weather-header {
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}

.location-icon {
  font-size: 0.9rem;
  color: var(--color-primary);
}

.weather-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
}

.weather-left {
  width: 45%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.weather-main-icon {
  width: 110px;
}

.weather-desc {
  font-size: 0.75rem;
  margin-top: 0.4rem;
  text-align: center;
}

.weather-right {
  width: 55%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
}

.weather-right .date {
  font-size: 0.85rem;
  color: #888;
  padding-bottom: 6px;
}

.weather-right .time {
  font-size: 1.2rem;
  font-weight: 600;
}

.weather-temp {
  font-size: 2.4rem;
  font-weight: 700;
}

.today-range {
  font-size: 0.75rem;
}

.today-range .hi {
  color: #e74c3c;
  font-weight: 600;
}

.today-range .lo {
  color: #3498db;
  font-weight: 600;
}

.week-strip {
  display: flex;
  justify-content: space-between;
  gap: 6px;
  margin-top: 8px;
}

.week-day {
  flex: 1;
  text-align: center;
}

.week-name {
  font-size: 0.72rem;
  color: #666;
  margin-bottom: 4px;
}

.week-icon {
  width: 42px;
  height: 42px;
}

.week-temp {
  font-size: 0.72rem;
}

.week-temp .hi {
  color: #e74c3c;
}

.week-temp .lo {
  color: #3498db;
}
</style>